import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import Loader from '@educator/Composite/QuestionCreation/Loader';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import {
  fetchQuizAnalytics,
  fetchQuizMinimalData,
  finishSection
} from '@actions/quizTaking';
import {
  getCurrentSessionUID,
  getIsPendingAction,
  getQuizDetails
} from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { useCommonData } from '@stores/AppCommonData';
import { ATTEMPT_SUMMARY_TYPE } from '@cont/Quiz/constants';
import useFinishQuiz from '@cont/Quiz/Taking/Common/useFinishQuiz';
import useFailedAPIQueue from '@cont/Quiz/Taking/Common/useFailedAPIQueue';
import useOngoingSection from '@cont/Quiz/Taking/Common/useOngoingSection';
import pushRoute from '@utils/pushRoute';
import useLanding from '../../Taking/Landing/useLanding';
import NtaHeader, { NTA_HEADER_VARIANT } from '../components/NtaHeader';
import { NTA_COLORS, NTA_SUBMIT_STEP } from '../constants';
import { ntaPageCss } from '../typography';
import NtaSubmitConfirmPage from '../PostSubmit/NtaSubmitConfirmPage';
import NtaSubmitSuccessPage from '../PostSubmit/NtaSubmitSuccessPage';
import NtaResultPage from '../PostSubmit/NtaResultPage';
import NtaSectionBar from './NtaSectionBar';
import NtaQuestionView from './NtaQuestionView';
import NtaNavigation from './NtaNavigation';
import NtaPaletteCollapse from './NtaPaletteCollapse';
import NtaExamSummaryPage from './NtaExamSummaryPage';
import { NtaExamSummaryProvider } from './NtaExamSummaryContext';
import NtaModals from './NtaModals';

const Modals = NtaModals;

const Page = styled.div`
  width: 100vw;
  height: 100vh;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  background: ${NTA_COLORS.white};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  ${ntaPageCss}
`;

const Body = styled.div`
  display: flex;
  align-items: stretch;
  flex: 1;
  width: 100%;
  min-height: 0;
  overflow: hidden;
  padding: 0 24px 24px;
  box-sizing: border-box;
  gap: ${({ $paletteOpen }) => ($paletteOpen ? '16px' : '0')};
`;

const NtaTakingLanding = () => {
  const dispatch = useDispatch();
  const { query } = useRouter();
  const { quizUID, quizSlug } = query;
  const [isPaletteCollapsed, setIsPaletteCollapsed] = useState(false);
  const [submitStep, setSubmitStep] = useState(null);
  const [isExamSummaryOpen, setIsExamSummaryOpen] = useState(false);
  const [isTimeoutSummary, setIsTimeoutSummary] = useState(false);
  const [summaryType, setSummaryType] = useState(ATTEMPT_SUMMARY_TYPE.TEST);
  const examSummaryContextValue = useMemo(
    () => ({
      summaryType,
      isTimeoutSummary,
      openSummary: (type = ATTEMPT_SUMMARY_TYPE.TEST) => {
        setIsTimeoutSummary(false);
        setSummaryType(type);
        setIsExamSummaryOpen(true);
      },
      closeSummary: () => {
        if (isTimeoutSummary) return;
        setIsExamSummaryOpen(false);
      }
    }),
    [isTimeoutSummary, summaryType]
  );

  const shouldShowTimer =
    submitStep !== NTA_SUBMIT_STEP.SUCCESS &&
    submitStep !== NTA_SUBMIT_STEP.RESULT;

  const { hasFetchedQuizQuestions } = useLanding();
  const {
    finishQuiz: finishSectionOrTest,
    handleEndTest,
    handleCleanUpAfterFinish,
    sendAnalyticsOnFinish
  } = useFinishQuiz();
  const { forceProcessAPISImmediately } = useFailedAPIQueue();
  const { hasOngoingSectional, isLastOngoingSection, onGoingSectionUID } =
    useOngoingSection();
  const currentSessionUID = useSelectorWithShallowEqual(getCurrentSessionUID);
  const isPendingAction = useSelectorWithShallowEqual(getIsPendingAction);
  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const currentPageInfo = useCommonData((state) => state.currentPageInfo);
  const setPageInfo = useCommonData((state) => state.setPageInfo);

  useEffect(() => {
    setPageInfo({
      ...currentPageInfo,
      layoutOptions: {
        ...currentPageInfo.layoutOptions,
        hideHeader: true,
        hideIntercom: true,
        hideFreshbot: true
      }
    });

    return () => {
      setPageInfo({
        ...currentPageInfo,
        layoutOptions: {
          ...currentPageInfo.layoutOptions,
          hideHeader: false
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTimerExpire = useCallback(async () => {
    await forceProcessAPISImmediately();

    if (hasOngoingSectional && !isLastOngoingSection) {
      await dispatch(
        finishSection(quizUID, currentSessionUID, onGoingSectionUID)
      );
      return;
    }

    try {
      await handleEndTest();
    } catch {
      return;
    }

    await dispatch(fetchQuizMinimalData(quizUID));
    if (currentSessionUID) {
      await dispatch(fetchQuizAnalytics(quizUID, currentSessionUID));
    }
    sendAnalyticsOnFinish();

    setSummaryType(ATTEMPT_SUMMARY_TYPE.TEST);
    setIsTimeoutSummary(true);
    setIsExamSummaryOpen(true);
  }, [
    currentSessionUID,
    dispatch,
    forceProcessAPISImmediately,
    handleEndTest,
    hasOngoingSectional,
    isLastOngoingSection,
    onGoingSectionUID,
    quizUID,
    sendAnalyticsOnFinish
  ]);

  const handleProceedToConfirm = () => {
    setSubmitStep(NTA_SUBMIT_STEP.CONFIRM);
  };

  const handleTimeoutProceed = () => {
    setIsExamSummaryOpen(false);
    setIsTimeoutSummary(false);
    setSubmitStep(NTA_SUBMIT_STEP.SUCCESS);
  };

  const handleSubmitSection = async () => {
    await finishSectionOrTest();
  };

  const handleConfirmCancel = () => {
    setSubmitStep(null);
    setIsExamSummaryOpen(true);
  };

  const handleConfirmOk = async () => {
    if (isPendingAction) return;

    const isLive =
      quizDetails?.quiz_over_in > 0 && quizDetails?.time_remaining <= 0;

    try {
      await handleEndTest();
    } catch {
      return;
    }

    if (isLive) {
      await handleCleanUpAfterFinish();
      return;
    }

    await dispatch(fetchQuizMinimalData(quizUID));

    if (currentSessionUID) {
      await dispatch(fetchQuizAnalytics(quizUID, currentSessionUID));
    }

    sendAnalyticsOnFinish();
    setSubmitStep(NTA_SUBMIT_STEP.SUCCESS);
  };

  const handleViewResult = () => {
    setSubmitStep(NTA_SUBMIT_STEP.RESULT);
  };

  const handleResultBack = async () => {
    await handleCleanUpAfterFinish();
    setSubmitStep(null);
    setIsExamSummaryOpen(false);
    setIsTimeoutSummary(false);

    pushRoute(
      {
        pathname: '/quiz/[quizSlug]/[quizUID]',
        query: {
          quizSlug: quizSlug || quizDetails?.slug,
          quizUID
        }
      },
      undefined,
      { shallow: true }
    );
  };

  if (!hasFetchedQuizQuestions) {
    return <Loader />;
  }

  let body = null;

  if (submitStep === NTA_SUBMIT_STEP.CONFIRM) {
    body = (
      <NtaSubmitConfirmPage
        isPending={isPendingAction}
        onConfirm={handleConfirmOk}
        onCancel={handleConfirmCancel}
      />
    );
  } else if (submitStep === NTA_SUBMIT_STEP.SUCCESS) {
    body = <NtaSubmitSuccessPage onViewResult={handleViewResult} />;
  } else if (submitStep === NTA_SUBMIT_STEP.RESULT) {
    body = <NtaResultPage onBack={handleResultBack} />;
  } else if (isExamSummaryOpen) {
    body = (
      <NtaExamSummaryPage
        onProceedToConfirm={handleProceedToConfirm}
        onSubmitSection={handleSubmitSection}
        onTimeoutProceed={handleTimeoutProceed}
      />
    );
  } else {
    body = (
      <>
        <NtaSectionBar />
        <Body $paletteOpen={!isPaletteCollapsed}>
          <NtaQuestionView />
          <NtaPaletteCollapse
            collapsed={isPaletteCollapsed}
            onToggle={() => setIsPaletteCollapsed((prev) => !prev)}
          />
          {!isPaletteCollapsed ? <NtaNavigation /> : null}
        </Body>
      </>
    );
  }

  return (
    <NtaExamSummaryProvider value={examSummaryContextValue}>
      <Page>
        <NtaHeader
          variant={NTA_HEADER_VARIANT.TEST}
          showTimer={shouldShowTimer}
          onTimerExpire={handleTimerExpire}
        />
        {body}
      </Page>
      <Modals />
    </NtaExamSummaryProvider>
  );
};

export default NtaTakingLanding;
